import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Calendar,
  Filter,
  Download,
  Loader,
  Users,
  HandCoins,
  CheckCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  X,
  Search,
} from "lucide-react";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

// ─────────────────────────────────────────────────────────────────────────────
// Export helpers
// ─────────────────────────────────────────────────────────────────────────────
function exportAdminToExcel(rows) {
  const data = rows.map((t) => ({
    Date: new Date(t.date_transaction).toLocaleDateString("fr-FR"),
    Référence: t.refCommande,
    Partenaire: t.partnerLibelle,
    "Nom Partenaire": `${t.partnerPrenom} ${t.partnerNom}`.trim(),
    Client: t.clientName,
    "Email Client": t.clientEmail || "",
    Arrivée: new Date(t.checkIn).toLocaleDateString("fr-FR"),
    Départ: new Date(t.checkOut).toLocaleDateString("fr-FR"),
    Chambres: t.rooms,
    Voyageurs: t.guests,
    "Base MyGO (TND)": t.prixBaseMygo,
    "Gain Admin (TND)": t.mygoProfit,
    "Gain Partenaire (TND)": t.margePartenaireAmount,
    "Prix Vente (TND)": t.montantVente,
    "Marge B2B (%)": t.margeB2b,
    Statut:
  t.status === "confirmed" ? "Confirmée" :
  t.status === "cancelled" ? "Annulée" :
  "En attente",
  }));

  const ws = XLSX.utils.json_to_sheet(data);
  ws["!cols"] = [
    { wch: 12 },
    { wch: 20 },
    { wch: 20 },
    { wch: 22 },
    { wch: 22 },
    { wch: 28 },
    { wch: 12 },
    { wch: 12 },
    { wch: 10 },
    { wch: 18 },
    { wch: 16 },
    { wch: 16 },
    { wch: 22 },
    { wch: 16 },
    { wch: 14 },
    { wch: 14 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Réservations");
  const date = new Date().toLocaleDateString("fr-FR").replace(/\//g, "-");
  XLSX.writeFile(wb, `Admin_Reservations_${date}.xlsx`);
}

function exportPartnerToExcel(rows) {
  const data = rows.map((t) => ({
    Date: new Date(t.date_transaction).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
    Type:
      t.type === "recharge"
        ? "Rechargement"
        : t.type === "booking"
          ? "Réservation"
          : t.type === "refund"
            ? "Remboursement"
            : t.type === "adjustment"
              ? "Ajustement"
              : t.type,
    Description: t.description || "",
    Référence: t.reference || "",
    "Marge (%)": t.margin_used != null ? parseFloat(t.margin_used) : "",
    "Montant (TND)": parseFloat(t.amount),
    "Profit (TND)": parseFloat(t.profit || 0) || "",
    "Solde Après (TND)": parseFloat(t.balance_after),
  }));

  const ws = XLSX.utils.json_to_sheet(data);
  ws["!cols"] = [
    { wch: 18 },
    { wch: 16 },
    { wch: 36 },
    { wch: 22 },
    { wch: 12 },
    { wch: 14 },
    { wch: 14 },
    { wch: 16 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Transactions");
  const date = new Date().toLocaleDateString("fr-FR").replace(/\//g, "-");
  XLSX.writeFile(wb, `Admin_Transactions_${date}.xlsx`);
}

async function exportRechargeHistoryToExcel() {
  try {
    const token = localStorage.getItem("authToken");
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recharge-requests`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) throw new Error("Erreur lors du chargement des demandes");

    const data = await response.json();
    const rows = data.requests || [];

    const exportData = rows.map((r) => ({
      "Date Demande": new Date(r.requested_at).toLocaleString("fr-FR"),
      "Partenaire": r.libelle_partenaire || "",
      "Nom": `${r.prenom || ""} ${r.nom || ""}`.trim(),
      "Email": r.email || "",
      "Montant (DT)": parseFloat(r.amount).toFixed(3),
      "Méthode Paiement": r.payment_method || "-",
      "Statut":
        r.status === "approved" ? "Approuvé" :
        r.status === "rejected" ? "Rejeté" :
        "En attente",
      "Traité le": r.processed_at
        ? new Date(r.processed_at).toLocaleString("fr-FR")
        : "-",
      "Notes Admin": r.admin_notes || "-",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    ws["!cols"] = [
      { wch: 20 }, // Date Demande
      { wch: 22 }, // Partenaire
      { wch: 22 }, // Nom
      { wch: 28 }, // Email
      { wch: 14 }, // Montant
      { wch: 20 }, // Méthode Paiement
      { wch: 14 }, // Statut
      { wch: 20 }, // Traité le
      { wch: 36 }, // Notes Admin
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Historique Soldes");

    const date = new Date().toLocaleDateString("fr-FR").replace(/\//g, "-");
    XLSX.writeFile(wb, `Admin_Historique_Soldes_${date}.xlsx`);

  } catch (error) {
    Swal.fire({
      icon: "error",
      title: "Erreur",
      text: "Impossible d'exporter l'historique des soldes",
      confirmButtonColor: "#FF7B47",
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Styled Date Picker Component — uses portal + fixed positioning
// so it escapes any parent overflow:hidden container
// ─────────────────────────────────────────────────────────────────────────────
function DatePicker({
  value,
  onChange,
  placeholder = "Sélectionner une date",
}) {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(value || new Date());
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close on outside click — checks both trigger and dropdown
  useEffect(() => {
    const handler = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Sync viewDate when value changes externally
  useEffect(() => {
    if (value) setViewDate(new Date(value));
  }, [value]);

  const MONTHS = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];
  const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDayRaw = new Date(year, month, 1).getDay();
  const firstDay = firstDayRaw === 0 ? 6 : firstDayRaw - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const selectDay = (day) => {
    onChange(new Date(year, month, day));
    setOpen(false);
  };

  const clearDate = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  const handleTriggerClick = () => {
    if (!open) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setOpen((o) => !o);
  };

  const isSelected = (day) => {
    if (!value) return false;
    const d = new Date(value);
    return (
      d.getFullYear() === year && d.getMonth() === month && d.getDate() === day
    );
  };

  const isToday = (day) => {
    const t = new Date();
    return (
      t.getFullYear() === year && t.getMonth() === month && t.getDate() === day
    );
  };

  const displayValue = value
    ? new Date(value).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : null;

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const calendar = (
    <div
      ref={dropdownRef}
      style={{
        position: "absolute",
        top: dropdownPos.top,
        left: dropdownPos.left,
        zIndex: 9999,
        background: "white",
        borderRadius: "16px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        padding: "20px",
        width: "300px",
        animation: "fadeSlideDown 0.15s ease",
      }}
    >
      <style>{`
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Month navigation */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <button
          type="button"
          onClick={prevMonth}
          style={{
            border: "none",
            background: "#f3f4f6",
            borderRadius: "8px",
            padding: "6px 10px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#FF7B47")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#f3f4f6")}
        >
          <ChevronLeft size={16} />
        </button>

        <span style={{ fontWeight: 700, color: "#2B4979", fontSize: "15px" }}>
          {MONTHS[month]} {year}
        </span>

        <button
          type="button"
          onClick={nextMonth}
          style={{
            border: "none",
            background: "#f3f4f6",
            borderRadius: "8px",
            padding: "6px 10px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#FF7B47")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#f3f4f6")}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Day headers */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "2px",
          marginBottom: "8px",
        }}
      >
        {DAYS.map((d) => (
          <div
            key={d}
            style={{
              textAlign: "center",
              fontSize: "11px",
              fontWeight: 700,
              color: "#9ca3af",
              padding: "4px 0",
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "2px",
        }}
      >
        {cells.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} />;
          const selected = isSelected(day);
          const today = isToday(day);
          return (
            <button
              key={day}
              type="button"
              onClick={() => selectDay(day)}
              style={{
                border: "none",
                borderRadius: "8px",
                padding: "8px 4px",
                fontSize: "13px",
                fontWeight: selected ? 700 : today ? 600 : 400,
                cursor: "pointer",
                background: selected
                  ? "#FF7B47"
                  : today
                    ? "#fff3ee"
                    : "transparent",
                color: selected ? "white" : today ? "#FF7B47" : "#374151",
                transition: "all 0.15s",
                outline: today && !selected ? "2px solid #FF7B47" : "none",
                outlineOffset: "-2px",
              }}
              onMouseEnter={(e) => {
                if (!selected) {
                  e.currentTarget.style.background = "#fff3ee";
                  e.currentTarget.style.color = "#FF7B47";
                }
              }}
              onMouseLeave={(e) => {
                if (!selected) {
                  e.currentTarget.style.background = today
                    ? "#fff3ee"
                    : "transparent";
                  e.currentTarget.style.color = today ? "#FF7B47" : "#374151";
                }
              }}
            >
              {day}
            </button>
          );
        })}
      </div>

      {/* Today shortcut */}
      <div
        style={{
          marginTop: "16px",
          borderTop: "1px solid #f3f4f6",
          paddingTop: "12px",
        }}
      >
        <button
          type="button"
          onClick={() => {
            onChange(new Date());
            setOpen(false);
          }}
          style={{
            width: "100%",
            border: "2px solid #FF7B47",
            borderRadius: "8px",
            background: "transparent",
            color: "#FF7B47",
            fontWeight: 600,
            padding: "8px",
            cursor: "pointer",
            fontSize: "13px",
            transition: "all 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#FF7B47";
            e.currentTarget.style.color = "white";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "#FF7B47";
          }}
        >
          Aujourd'hui
        </button>
      </div>
    </div>
  );

  return (
    <div
      ref={triggerRef}
      style={{ position: "relative", display: "inline-block", width: "100%" }}
    >
      {/* Trigger button */}
      <button
        type="button"
        onClick={handleTriggerClick}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "10px 16px",
          border: open ? "2px solid #FF7B47" : "2px solid #e5e7eb",
          borderRadius: "10px",
          background: "white",
          cursor: "pointer",
          transition: "border-color 0.2s",
          fontSize: "14px",
          color: displayValue ? "#1f2937" : "#9ca3af",
          fontFamily: "inherit",
        }}
      >
        <Calendar size={16} color={open ? "#FF7B47" : "#9ca3af"} />
        <span style={{ flex: 1, textAlign: "left" }}>
          {displayValue || placeholder}
        </span>
        {value && (
          <span
            onClick={clearDate}
            style={{
              display: "flex",
              alignItems: "center",
              color: "#9ca3af",
              cursor: "pointer",
              padding: "2px",
              borderRadius: "50%",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#FF7B47")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
          >
            <X size={14} />
          </span>
        )}
      </button>

      {/* Portal — renders at document.body, escapes all overflow:hidden parents */}
      {open && createPortal(calendar, document.body)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
export default function TransactionHistoryPage({ onBack, userData }) {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterDate, setFilterDate] = useState(null);
  const [adminSearch, setAdminSearch] = useState("");

  const [stats, setStats] = useState({
    total: 0,
    totalProfit: 0,
    confirmed: 0,
    pending: 0,
  });

  const isAdmin = !!userData?.is_admin;

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("authToken");
      const endpoint = isAdmin
        ? `${process.env.REACT_APP_API_URL}/api/transactions/admin/all`
        : `${process.env.REACT_APP_API_URL}/api/transactions`; 

      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("Erreur lors du chargement");
      const data = await response.json();

      if (isAdmin) {
        const transformed = (data.reservations || []).map((r) => ({
          id: r.id_resa,
          refCommande: r.ref_commande,
          date_transaction: r.date_resa,
          checkIn: r.date_arr,
          checkOut: r.date_dep,
          guests: `${r.nb_adl} adulte(s)${r.nb_enf > 0 ? `, ${r.nb_enf} enfant(s)` : ""}`,
          rooms: r.nb_room,
          partnerLibelle: r.libelle_partenaire,
          partnerNom: r.nom_partenaire,
          partnerPrenom: r.prenom_partenaire,
          clientName: `${r.client_prenom || ""} ${r.client_nom || ""}`.trim(),
          clientEmail: r.client_mail,
          prixBaseMygo: parseFloat(r.prix_base_mygo || 0),
          mygoProfit: parseFloat(r.mygo_profit || 0),
          montantAchatPartenaire: parseFloat(r.montant_achat_partenaire || 0),
          margePartenaireAmount: parseFloat(r.marge_partenaire_montant || 0),
          montantVente: parseFloat(r.montant_vente || 0),
          margeB2b: parseFloat(r.marge_b2b || 0),
          status:
            r.etat_commande === "Valider"
              ? "confirmed"
              : r.etat_commande === "Annuler"
                ? "cancelled"
                : "pending",
          paymentStatus: r.etat_paiement,
        }));

        setTransactions(transformed);
        setStats({
          total: transformed.length,
          totalProfit: transformed
  .filter((t) => t.status === 'confirmed')
  .reduce((s, t) => s + t.mygoProfit, 0),
          confirmed: transformed.filter((t) => t.status === "confirmed").length,
          pending: transformed.filter((t) => t.status === "pending").length,
          cancelled: transformed.filter((t) => t.status === "cancelled").length,
        });
      } else {
        setTransactions(data.transactions || []);
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Impossible de charger l'historique",
        confirmButtonColor: "#FF7B47",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getTypeConfig = (type) => {
    switch (type) {
      case "recharge":
        return {
          label: "Rechargement",
          color: "bg-green-100 text-green-800",
          icon: <TrendingUp size={16} />,
        };
      case "booking":
        return {
          label: "Réservation",
          color: "bg-blue-100 text-blue-800",
          icon: <TrendingDown size={16} />,
        };
      case "refund":
        return {
          label: "Remboursement",
          color: "bg-yellow-100 text-yellow-800",
          icon: <TrendingUp size={16} />,
        };
      case "adjustment":
        return {
          label: "Ajustement",
          color: "bg-purple-100 text-purple-800",
          icon: <TrendingUp size={16} />,
        };
      default:
        return {
          label: type,
          color: "bg-gray-100 text-gray-800",
          icon: <TrendingUp size={16} />,
        };
    }
  };

  const matchesDate = (dateStr) => {
    if (!filterDate) return true;
    return (
      new Date(dateStr).toDateString() === new Date(filterDate).toDateString()
    );
  };

  // ── Filtered lists ─────────────────────────────────────────────────
  const filteredTransactions = isAdmin
    ? transactions.filter((t) => {
        const search = adminSearch.toLowerCase();
        const matchesSearch =
          !search ||
          (t.partnerLibelle || "").toLowerCase().includes(search) ||
          (t.partnerNom || "").toLowerCase().includes(search) ||
          (t.partnerPrenom || "").toLowerCase().includes(search) ||
          (t.clientName || "").toLowerCase().includes(search) ||
          (t.clientEmail || "").toLowerCase().includes(search) ||
          (t.refCommande || "").toLowerCase().includes(search);
        return matchesSearch && matchesDate(t.date_transaction);
      })
    : transactions.filter((t) => {
        if (filterType !== "all" && t.type !== filterType) return false;
        return matchesDate(t.date_transaction);
      });

  // ── Partner stats ──────────────────────────────────────────────────
  const totalDebited = filteredTransactions
    .filter((t) => parseFloat(t.amount) < 0)
    .reduce((s, t) => s + Math.abs(parseFloat(t.amount)), 0);
  const totalCredited = filteredTransactions
    .filter((t) => parseFloat(t.amount) > 0)
    .reduce((s, t) => s + parseFloat(t.amount), 0);
  const totalPartnerProfit = filteredTransactions.reduce(
    (s, t) => s + parseFloat(t.profit || 0),
    0,
  );

  // ── Admin stats (filtered) ─────────────────────────────────────────
const totalMygoProfit = filteredTransactions
  .filter((t) => t.status === 'confirmed')
  .reduce((s, t) => s + parseFloat(t.mygoProfit || 0), 0);
  
  const confirmedCount = filteredTransactions.filter(
    (t) => t.status === "confirmed",
  ).length;
  const pendingCount = filteredTransactions.filter(
    (t) => t.status === "pending",
  ).length;

  const hasActiveFilters = filterDate || adminSearch || filterType !== "all";

  // ══════════════════════════════════════════════════════════════════
  // ADMIN VIEW
  // ══════════════════════════════════════════════════════════════════
  if (isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2B4979] via-[#3d5c8f] to-[#2B4979] py-12 px-4">
        <div className="container mx-auto max-w-7xl">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-white hover:text-gray-200 transition mb-6"
          >
            <ArrowLeft size={20} />
            <span className="font-semibold">Retour</span>
          </button>

          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-red-600 to-red-700 p-8 text-white">
              <h1 className="text-3xl font-bold mb-2">
                Tableau de Bord Admin — Réservations
              </h1>
              <p className="text-red-100">
                Toutes les réservations partenaires · Revenu MyGO
              </p>
            </div>

            {/* Admin Stats */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-6">
              {[
                {
                  label: "Total Réservations",
                  value: filteredTransactions.length,
                  color: "blue",
                  icon: <Calendar size={32} className="text-blue-400" />,
                },
                {
                  label: "Revenu MyGO Total",
                  value: `${totalMygoProfit.toFixed(3)} TND`,
                  color: "green",
                  icon: <HandCoins size={32} className="text-green-400" />,
                },
                {
                  label: "Confirmées",
                  value: confirmedCount,
                  color: "emerald",
                  icon: <CheckCircle size={32} className="text-emerald-400" />,
                },
                {
                  label: "En Attente",
                  value: pendingCount,
                  color: "orange",
                  icon: <Clock size={32} className="text-orange-400" />,
                },
                  {                                                         
                  label: "Annulées",
                  value: filteredTransactions.filter((t) => t.status === "cancelled").length,
                  color: "red",
                  icon: <X size={32} className="text-red-400" />,
                },
              ].map(({ label, value, color, icon }) => (
                <div
                  key={label}
                  className={`bg-${color}-50 border-l-4 border-${color}-500 p-4 rounded-lg`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className={`text-sm text-${color}-600 font-semibold mb-1`}
                      >
                        {label}
                      </p>
                      <p className={`text-2xl font-bold text-${color}-700`}>
                        {value}
                      </p>
                    </div>
                    {icon}
                  </div>
                </div>
              ))}
            </div>

            {/* Admin Filters */}
            <div className="p-6 border-t bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Filter size={20} className="text-gray-600" />
                  <span className="font-semibold text-gray-700">Filtres</span>
                </div>
                {hasActiveFilters && (
                  <button
                    onClick={() => {
                      setFilterDate(null);
                      setAdminSearch("");
                    }}
                    className="text-sm text-[#FF7B47] hover:text-[#ff6a30] font-semibold flex items-center gap-1 transition"
                  >
                    <X size={14} /> Réinitialiser
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Combined search */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Rechercher partenaire / client / référence
                  </label>
                  <div className="relative">
                    <Search
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      value={adminSearch}
                      onChange={(e) => setAdminSearch(e.target.value)}
                      placeholder="Nom, email, référence..."
                      className="w-full pl-9 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#FF7B47] focus:outline-none text-sm transition"
                    />
                    {adminSearch && (
                      <button
                        onClick={() => setAdminSearch("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#FF7B47] transition"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Styled date picker */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Filtrer par date
                  </label>
<div className="custom-datepicker">
  <DatePicker
    value={filterDate}
    onChange={setFilterDate}
    placeholder="Choisir une date..."
  />
</div>
                </div>
              </div>
            </div>
          </div>

          {/* Admin Table */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="animate-spin text-[#FF7B47]" size={48} />
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Calendar size={64} className="mx-auto mb-4 opacity-30" />
                <p className="text-xl">Aucune réservation trouvée</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-red-600 text-white">
                    <tr>
                      {[
                        "Date",
                        "Réf",
                        "Partenaire",
                        "Client",
                        "Séjour",
                        "Base MyGO\n(prix net)",
                        "Gain Admin\n(marge b2b)",
                        "Gain Partenaire\n(marge partenaire)",
                        "Prix Vente\n(client)",
                        "Statut",
                      ].map((h, i) => (
                        <th
                          key={i}
                          className="px-4 py-4 text-left text-xs font-semibold uppercase whitespace-pre-line"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredTransactions.map((t) => (
                      <tr key={t.id} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {new Date(t.date_transaction).toLocaleDateString(
                            "fr-FR",
                            {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            },
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium text-[#2B4979]">
                          {t.refCommande}
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <div className="flex items-center gap-2">
                            <Users size={14} className="text-gray-400" />
                            <div>
                              <div className="font-semibold text-gray-900">
                                {t.partnerLibelle}
                              </div>
                              <div className="text-xs text-gray-500">
                                {t.partnerPrenom} {t.partnerNom}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <div className="font-medium text-gray-900">
                            {t.clientName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {t.clientEmail}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          <div>
                            {new Date(t.checkIn).toLocaleDateString("fr-FR")}
                          </div>
                          <div className="text-xs text-gray-400">
                            → {new Date(t.checkOut).toLocaleDateString("fr-FR")}
                          </div>
                          <div className="text-xs text-gray-400">
                            {t.rooms} ch. · {t.guests}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-right font-medium text-gray-900">
                          {t.prixBaseMygo.toFixed(3)} TND
                        </td>
                        <td className="px-4 py-4 text-sm text-right font-bold text-yellow-600">
                          +{t.mygoProfit.toFixed(3)} TND
                        </td>
                        <td className="px-4 py-4 text-sm text-right font-medium text-blue-600">
                          +{t.margePartenaireAmount.toFixed(3)} TND
                        </td>
                        <td className="px-4 py-4 text-sm text-right font-bold text-[#2B4979]">
                          {t.montantVente.toFixed(3)} TND
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                              t.status === "confirmed"
                                ? "bg-green-100 text-green-700"
                                : t.status === "cancelled"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-orange-100 text-orange-700"
                            }`}
                          >
                            {t.status === "confirmed" ? (
                              <>
                                <CheckCircle size={12} /> Confirmée
                              </>
                            ) : t.status === "cancelled" ? (
                              <>
                                <X size={12} /> Annulée
                              </>
                            ) : (
                              <>
                                <Clock size={12} /> En attente
                              </>
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

{filteredTransactions.length > 0 && (
  <div className="border-t bg-gray-50 p-4 flex justify-between items-center">
    <p className="text-sm text-gray-600">
      <span className="font-bold">
        {filteredTransactions.length}
      </span>{" "}
      réservation(s) · Revenu Admin total:{" "}
      <span className="font-bold text-green-600">
        {totalMygoProfit.toFixed(3)} TND
      </span>
    </p>
    <div className="flex items-center gap-3">
      <button
        onClick={exportRechargeHistoryToExcel}
        className="flex items-center gap-2 bg-[#2B4979] hover:bg-[#1e3454] text-white px-4 py-2 rounded-lg transition font-semibold"
      >
        <Download size={18} /> Exporter Historique Soldes
      </button>
      <button
        onClick={() => exportAdminToExcel(filteredTransactions)}
        className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition font-semibold"
      >
        <Download size={18} /> Exporter Réservations
      </button>
    </div>
  </div>
)}
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // PARTNER VIEW
  // ══════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2B4979] via-[#3d5c8f] to-[#2B4979] py-12 px-4">
      <div className="container mx-auto max-w-7xl">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white hover:text-gray-200 transition mb-6"
        >
          <ArrowLeft size={20} />
          <span className="font-semibold">Retour</span>
        </button>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-[#2B4979] to-[#3d5c8f] p-8 text-white">
            <h1 className="text-3xl font-bold mb-2">
              Historique des Transactions
            </h1>
            <p className="text-blue-100">Suivez tous vos mouvements de solde</p>
          </div>

          {/* Partner Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6">
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 font-semibold mb-1">
                    Total Dépensé
                  </p>
                  <p className="text-2xl font-bold text-red-700">
                    {totalDebited.toFixed(3)} TND
                  </p>
                </div>
                <TrendingDown size={32} className="text-red-400" />
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-semibold mb-1">
                    Total Rechargé
                  </p>
                  <p className="text-2xl font-bold text-green-700">
                    {totalCredited.toFixed(3)} TND
                  </p>
                </div>
                <TrendingUp size={32} className="text-green-400" />
              </div>
            </div>
            <div className="bg-[#FF7B47]/10 border-l-4 border-[#FF7B47] p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#FF7B47] font-semibold mb-1">
                    Total Profit
                  </p>
                  <p className="text-2xl font-bold text-[#FF7B47]">
                    {totalPartnerProfit.toFixed(3)} TND
                  </p>
                </div>
                <TrendingUp size={32} className="text-[#FF7B47]/50" />
              </div>
            </div>
          </div>

          {/* Partner Filters */}
          <div className="p-6 border-t bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter size={20} className="text-gray-600" />
                <span className="font-semibold text-gray-700">Filtres</span>
              </div>
              {hasActiveFilters && (
                <button
                  onClick={() => {
                    setFilterType("all");
                    setFilterDate(null);
                  }}
                  className="text-sm text-[#FF7B47] hover:text-[#ff6a30] font-semibold flex items-center gap-1 transition"
                >
                  <X size={14} /> Réinitialiser
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Type
                </label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#FF7B47] transition text-sm"
                >
                  <option value="all">Tous les types</option>
                  <option value="recharge">Rechargements</option>
                  <option value="booking">Réservations</option>
                  <option value="refund">Remboursements</option>
                  <option value="adjustment">Ajustements</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Filtrer par date
                </label>
                <div className="custom-datepicker">
                <DatePicker
                  value={filterDate}
                  onChange={setFilterDate}
                  placeholder="Choisir une date..."
                />
                </div>  
              </div>
            </div>
          </div>
        </div>

        {/* Partner Table */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="animate-spin text-[#FF7B47]" size={48} />
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Calendar size={64} className="mx-auto mb-4 opacity-30" />
              <p className="text-xl">Aucune transaction trouvée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100 border-b-2 border-gray-200">
                  <tr>
                    {[
                      "Date",
                      "Type",
                      "Description",
                      "Marge",
                      "Montant",
                      "Profit",
                      "Solde Après",
                    ].map((h) => (
                      <th
                        key={h}
                        className={`px-6 py-4 text-xs font-semibold text-gray-600 uppercase ${h === "Marge" ? "text-center" : h === "Date" || h === "Type" || h === "Description" ? "text-left" : "text-right"}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTransactions.map((transaction) => {
                    const typeConfig = getTypeConfig(transaction.type);
                    const amount = parseFloat(transaction.amount);
                    const profit = parseFloat(transaction.profit || 0);
                    const isDebit = amount < 0;

                    return (
                      <tr
                        key={transaction.id_transaction}
                        className="hover:bg-gray-50 transition"
                      >
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {new Date(
                            transaction.date_transaction,
                          ).toLocaleDateString("fr-FR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 w-fit ${typeConfig.color}`}
                          >
                            {typeConfig.icon}
                            {typeConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          <p className="font-semibold">
                            {transaction.description || "N/A"}
                          </p>
                          {transaction.reference && (
                            <p className="text-xs text-gray-500">
                              Réf: {transaction.reference}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center text-sm">
                          {transaction.margin_used != null ? (
                            <span className="font-semibold text-[#FF7B47]">
                              {parseFloat(transaction.margin_used).toFixed(0)}%
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right text-sm">
                          <span
                            className={`font-bold ${isDebit ? "text-red-600" : "text-green-600"}`}
                          >
                            {isDebit ? "" : "+"}
                            {amount.toFixed(3)} TND
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-sm">
                          {profit > 0 ? (
                            <span className="font-bold text-green-600">
                              +{profit.toFixed(3)} TND
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                          {parseFloat(transaction.balance_after).toFixed(3)} TND
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {filteredTransactions.length > 0 && (
            <div className="border-t bg-gray-50 p-4 flex justify-between items-center">
              <p className="text-sm text-gray-600">
                <span className="font-bold">{filteredTransactions.length}</span>{" "}
                transaction(s) affichée(s)
              </p>
              <button
                onClick={() => exportPartnerToExcel(filteredTransactions)}
                className="flex items-center gap-2 bg-[#2B4979] hover:bg-[#1e3454] text-white px-4 py-2 rounded-lg transition font-semibold"
              >
                <Download size={18} /> Exporter Excel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
